import {Injectable} from '@angular/core';
import {BehaviorSubject, distinctUntilChanged, map, Observable} from 'rxjs';

/**
 * Blocks the whole app behind an overlay with a message while a long operation that must not be interrupted is
 * running (see the overlay in AppComponent). Operations can overlap, the app is released once all of them are done.
 */
@Injectable({providedIn: 'root'})
export class AppBlockerService {
  private readonly messages$ = new BehaviorSubject<string[]>([]);

  readonly active$: Observable<boolean> = this.messages$.pipe(
    map(messages => messages.length > 0),
    distinctUntilChanged()
  );

  /** Message of the operation that started last */
  readonly message$: Observable<string | null> = this.messages$.pipe(
    map(messages => messages[messages.length - 1] ?? null),
    distinctUntilChanged()
  );

  get active(): boolean {
    return this.messages$.value.length > 0;
  }

  /** Returns a function that ends this block, safe to call more than once */
  block(message: string): () => void {
    this.messages$.next([...this.messages$.value, message]);

    let released = false;
    return () => {
      if (released) return;
      released = true;
      const messages = [...this.messages$.value];
      messages.splice(messages.indexOf(message), 1);
      this.messages$.next(messages);
    };
  }
}
